interface StorageItem<T> {
  value: T;
  timestamp: number; // 存储时间（毫秒）
  ttl: number; // 有效期（毫秒）
  userId?: string; // 可选：用于用户隔离
}

class SafeStorage {
  private static instance: SafeStorage;
  private currentUserId: string | null = null;
  private readonly DEFAULT_TTL = 3600; // 1小时，单位秒
  private readonly KEY_PREFIX = 'safe_storage_'; // 统一前缀，避免冲突

  private constructor() {
    // 初始化时清理过期数据
    this.cleanExpiredItems();
  }

  static getInstance(): SafeStorage {
    if (!SafeStorage.instance) {
      SafeStorage.instance = new SafeStorage();
    }
    return SafeStorage.instance;
  }

  /**
   * 设置当前用户ID（登录后调用）
   */
  setCurrentUser(userId: string | null): void {
    this.currentUserId = userId;
  }

  /**
   * 获取当前用户ID
   */
  getCurrentUser(): string | null {
    return this.currentUserId;
  }

  /**
   * 生成带前缀和用户隔离的key
   */
  private generateKey(key: string): string {
    const userPrefix = this.currentUserId ? `${this.currentUserId}_` : 'guest_';
    return `${this.KEY_PREFIX}${userPrefix}${key}`;
  }

  /**
   * 清理过期数据
   */
  private cleanExpiredItems(): void {
    try {
      Object.keys(localStorage).forEach(key => {
        if (!key.startsWith(this.KEY_PREFIX)) return;
        
        const itemStr = localStorage.getItem(key);
        if (!itemStr) return;
        
        try {
          const item: StorageItem<unknown> = JSON.parse(itemStr);
          const now = Date.now();
          
          // 检查是否过期
          if (now - item.timestamp > item.ttl) {
            localStorage.removeItem(key);
          }
        } catch (e) {
          // 解析失败的数据也删除
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.warn('清理过期数据失败:', error);
    }
  }

  /**
   * 存储数据（带过期时间）
   * @param key 存储键
   * @param data 数据
   * @param ttlSeconds 过期时间（秒），默认 1 小时
   */
  set<T>(key: string, data: T, ttlSeconds: number = this.DEFAULT_TTL): void {
    if (!key) {
      console.warn('存储key不能为空');
      return;
    }
    
    const fullKey = this.generateKey(key);
    const item: StorageItem<T> = {
      value: data,
      timestamp: Date.now(),
      ttl: ttlSeconds * 1000,
      userId: this.currentUserId || undefined
    };
    
    try {
      localStorage.setItem(fullKey, JSON.stringify(item));
    } catch (error) {
      console.warn(`Failed to save to localStorage: ${fullKey}`, error);
      // 如果存储失败，尝试清理过期数据后再次尝试
      this.cleanExpiredItems();
      try {
        localStorage.setItem(fullKey, JSON.stringify(item));
      } catch (retryError) {
        console.error(`重试存储失败: ${fullKey}`, retryError);
      }
    }
  }

  /**
   * 获取数据（自动检查过期和用户匹配）
   */
  get<T>(key: string): T | null {
    if (!key) {
      console.warn('获取数据的key不能为空');
      return null;
    }
    
    const fullKey = this.generateKey(key);
    const itemStr = localStorage.getItem(fullKey);
    if (!itemStr) return null;

    try {
      const item: StorageItem<T> = JSON.parse(itemStr);
      const now = Date.now();

      // 检查是否过期
      if (now - item.timestamp > item.ttl) {
        this.remove(key);
        return null;
      }

      // 用户隔离检查（因为使用了key前缀，这里主要是双重保险）
      if (item.userId && item.userId !== this.currentUserId) {
        return null;
      }

      return item.value;
    } catch (error) {
      console.warn(`Failed to parse localStorage item: ${fullKey}`, error);
      this.remove(key);
      return null;
    }
  }

  /**
   * 删除指定 key
   */
  remove(key: string): void {
    if (!key) {
      console.warn('删除数据的key不能为空');
      return;
    }
    const fullKey = this.generateKey(key);
    localStorage.removeItem(fullKey);
  }

  /**
   * 检查key是否存在且未过期
   */
  has(key: string): boolean {
    return this.get(key) !== null;
  }

  /**
   * 获取数据的剩余有效时间（秒）
   */
  getRemainingTime(key: string): number {
    if (!key) return 0;
    
    const fullKey = this.generateKey(key);
    const itemStr = localStorage.getItem(fullKey);
    if (!itemStr) return 0;

    try {
      const item: StorageItem<unknown> = JSON.parse(itemStr);
      const now = Date.now();
      const remainingMs = item.ttl - (now - item.timestamp);
      return Math.max(0, Math.floor(remainingMs / 1000));
    } catch (error) {
      return 0;
    }
  }

  /**
   * 清除所有属于当前用户的数据（登出时调用）
   */
  clearForCurrentUser(): void {
    if (!this.currentUserId) {
      // 清理guest数据
      this.clearUserData('guest');
      return;
    }

    this.clearUserData(this.currentUserId);
  }

  /**
   * 清理指定用户的数据
   */
  private clearUserData(userId: string): void {
    const prefix = `${this.KEY_PREFIX}${userId}_`;
    
    try {
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith(prefix)) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.warn(`清理用户${userId}数据失败:`, error);
    }
  }

  /**
   * 获取当前用户所有存储的key列表
   */
  getAllKeys(): string[] {
    const userPrefix = this.currentUserId ? `${this.currentUserId}_` : 'guest_';
    const fullPrefix = `${this.KEY_PREFIX}${userPrefix}`;
    
    return Object.keys(localStorage)
      .filter(key => key.startsWith(fullPrefix))
      .map(key => key.substring(fullPrefix.length));
  }

  /**
   * 清空所有（谨慎使用）
   */
  clearAll(): void {
    localStorage.clear();
  }
}

// 导出单例实例
export const safeStorage = SafeStorage.getInstance();

export default SafeStorage;
